import { useTranslation } from "react-i18next";

import { Arrange, Link, Typography } from "@aviary";
import type { WordpressSpecialType } from "@emerson/data/queries/WordpressSpecialsQuery/WordpressSpecialsQuery.query";
import { l } from "@emerson/locales/i18n";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import * as styles from "./ShopSpecials.styles";

interface Props {
  onClose?: () => void;
  specials: WordpressSpecialType[];
  sideMenuCallbacks?: {
    onFocus: () => void;
    onBlur: () => void;
  };
}

const ShopSpecials = ({ onClose, sideMenuCallbacks, specials }: Props) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const onClick = (title: string) => {
    logAnalyticsEvent({
      eventName: isUnauthenticated
        ? AnalyticsEvent.EMERSON_UNAUTH_SHOP_MENU_CLICK_TO_NAVIGATE
        : AnalyticsEvent.EMERSON_SHOP_MENU_CLICK_TO_NAVIGATE,
      payload: {
        origin_url: location.pathname,
        category_name: t(l.header.Shop.Specials),
        subcategory_name: title,
        content_name: title,
        automatic_event: false,
      },
    });
  };

  return (
    <ul css={styles.specials} role="none">
      {specials?.map(special => (
        <li key={`special-${special.specialDisplayOrder}`} role="none">
          <Link
            to={special.specialActionUrl}
            isUnderLined={false}
            css={styles.special}
            aria-label={special.specialText}
            role="menuitem"
            onFocus={sideMenuCallbacks?.onFocus}
            onBlur={sideMenuCallbacks?.onBlur}
            onClick={() => {
              onClick(special.specialText);
              onClose?.();
            }}
          >
            <figure>
              <div css={styles.specialImageWrapper}>
                <img src={special?.specialBrandImage} alt={special.specialText} />
              </div>
              <figcaption>
                <Arrange
                  spacing="xxsmall"
                  alignment="end"
                  justify="spaceBetween"
                  isFullWidth
                  isFullHeight
                  isVertical
                >
                  <Typography
                    css={styles.title}
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    dangerouslySetInnerHTML={{ __html: special.specialText }}
                  />
                  <Typography>
                    {t(l.header.Shop.CouponCode)}: <strong>{special.specialPromoCode}</strong>
                  </Typography>
                </Arrange>
              </figcaption>
            </figure>
          </Link>
        </li>
      ))}
    </ul>
  );
};

export { ShopSpecials };
